import { Controller } from "@hotwired/stimulus"

// The guest's proposal page (Events v2). Reports the visit once (the server ignores staff and repeats), and keeps the
// estimate in step with the guest's picks: the link takes them (?picks=12,15) so a reload or a shared link shows the
// same choices, and the server prices them, so the page never does its own sums. Without this script the form's
// "Update total" button reloads the page with the picks. The sign dialog (party_sign_controller) sits in the same form
// and posts it all. Its details, its saved-card line and its footer total hang on the same numbers, so they come back
// with the total and move with it. The folded agreement opens to print. An add-on that goes with some menus carries
// their option ids (data-only-with): it turns off, unticked, while none of them is picked, and the line under the
// add-ons says which ones a changed pick took off, as the server does for the no-script reload.
export default class extends Controller {
  static targets = ["form", "update", "totals", "barTotal", "details", "cardLine", "signTotal", "dropped"]
  static values = { viewUrl: String, totalUrl: String }

  connect() {
    if (this.hasUpdateTarget) this.updateTarget.hidden = true
    if (this.hasViewUrlValue) this.reportView()
    this.openToPrint = () => this.element.querySelectorAll("details.pe-agreement").forEach((fold) => { fold.open = true })
    window.addEventListener("beforeprint", this.openToPrint)
  }

  disconnect() {
    this.request?.abort()
    window.removeEventListener("beforeprint", this.openToPrint)
  }

  reportView() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
    fetch(this.viewUrlValue, { method: "POST", headers: { "X-CSRF-Token": token }, credentials: "same-origin" })
      .catch(() => {})
  }

  pick(event) {
    if (!event.target.matches('[name="picks[]"], [name^="choice["]')) return

    this.offerAddOns()
    const checked = this.formTarget.querySelectorAll('[name="picks[]"]:checked, [name^="choice["]:checked')
    const picks = [...checked].map((input) => input.value).join(",")
    // An empty list stays in the link: it means "no add-ons", where no list at all means the restaurant's defaults.
    // Built from the form's own address: after a refused Accept the window shows the address it posted to.
    const page = new URL(this.formTarget.action)
    page.hash = window.location.hash
    page.searchParams.set("picks", picks)
    window.history.replaceState(window.history.state, "", page)
    this.refresh(picks)
  }

  offerAddOns() {
    const chosen = new Set([...this.formTarget.querySelectorAll('[name^="choice["]:checked')].map((input) => input.value))
    const removed = []
    this.formTarget.querySelectorAll("label[data-only-with]").forEach((tile) => {
      const input = tile.querySelector('input[name="picks[]"]')
      if (!input) return

      const offered = tile.dataset.onlyWith.split(" ").some((id) => chosen.has(id))
      if (!offered && input.checked) {
        input.checked = false
        removed.push(tile.dataset.removedLine)
      }
      input.disabled = !offered
      tile.classList.toggle("ef-tile--off", !offered)
    })
    if (this.hasDroppedTarget) this.droppedTarget.textContent = removed.join(" ")
  }

  // Enter in the name field submits through the form's first button, the hidden "Update total": the total is already
  // live, and a reload would drop what the guest typed.
  submit(event) {
    if (this.hasUpdateTarget && this.updateTarget.hidden && event.submitter === this.updateTarget) event.preventDefault()
  }

  async refresh(picks) {
    this.request?.abort()
    const request = new AbortController()
    this.request = request
    const url = new URL(this.totalUrlValue, window.location.href)
    url.searchParams.set("picks", picks)
    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(`Total failed: ${response.status}`)
      const fragment = document.createRange().createContextualFragment(await response.text())
      for (const name of ["details", "cardLine"]) {
        const part = fragment.querySelector(`[data-party-event-target="${name}"]`)
        const target = this.targets.find(name)
        if (part && target) {
          target.replaceWith(part)
        } else {
          part?.remove()
        }
      }
      const signTotal = fragment.querySelector('[data-party-event-target="signTotal"]')
      if (signTotal && this.hasSignTotalTarget) this.signTotalTarget.textContent = signTotal.textContent
      signTotal?.remove()
      this.totalsTarget.replaceChildren(fragment)
      const total = this.totalsTarget.querySelector(".pe-row--total dd")
      if (total && this.hasBarTotalTarget) this.barTotalTarget.textContent = total.textContent
    } catch (error) {
      if (error.name === "AbortError") return
      // The proposal may have changed or closed meanwhile (409), or the network dropped: the button reloads the page
      // as it stands.
      if (this.hasUpdateTarget) this.updateTarget.hidden = false
    }
  }
}
